import { isFood, isRoad, typesOf } from './partnerTypes';

/**
 * The words a business uses, as the server decided them (`partner.vocab`),
 * with a local fallback so no heading is ever blank.
 */
const FALLBACK = {
  food: { key: 'food', item: 'item', items: 'menu', addItem: 'Add item', catalogue: 'My menu', customerTab: 'Menu', sells: 'products', form: 'food', units: ['1 plate', '1 pc', '1 bowl', '1 glass', '1 cup'], orderVerb: 'Order' },
  hotel: { key: 'hotel', item: 'room', items: 'rooms', addItem: 'Add room', catalogue: 'My rooms', customerTab: 'Rooms', sells: 'rooms', form: 'hotel', units: ['per night'], orderVerb: 'Book' },
  travel: { key: 'travel', item: 'service', items: 'services', addItem: 'Add service', catalogue: 'My services', customerTab: 'Packages & services', sells: 'services', form: 'service', units: ['per person', 'per trip', 'per day', 'per km'], orderVerb: 'Book' },
  garage: { key: 'garage', item: 'service', items: 'services', addItem: 'Add service', catalogue: 'My services', customerTab: 'Services', sells: 'both', form: 'service', units: ['per service', 'per visit', 'per hour'], productUnits: ['1 pc', '1 set', '1 pair', '1 litre'], orderVerb: 'Book', askPickupDrop: true, customFields: true },
  hiring: { key: 'hiring', item: 'job', items: 'jobs', addItem: 'Post a job', catalogue: 'My jobs', customerTab: 'Jobs', sells: 'none', form: 'job', units: [], orderVerb: 'Apply' },
  showroom: { key: 'showroom', item: 'product', items: 'products', addItem: 'Add product', catalogue: 'My products', customerTab: 'Products', sells: 'products', form: 'showroom', units: ['1 pc', '1 set', '1 pair'], orderVerb: 'Order', customFields: true },
  road: { key: 'road', item: '', items: '', addItem: '', catalogue: '', customerTab: '', sells: 'none', form: 'vehicle', units: [], orderVerb: 'Book' },
  service: { key: 'service', item: 'service', items: 'services', addItem: 'Add service', catalogue: 'My services', customerTab: 'Services', sells: 'services', form: 'service', units: ['per service', 'per visit', 'per hour'], orderVerb: 'Book' },
  shop: { key: 'shop', item: 'product', items: 'products', addItem: 'Add product', catalogue: 'My products', customerTab: 'Products', sells: 'products', form: 'product', units: ['1 pc', '1 kg', '500 g', '1 litre'], orderVerb: 'Order', customFields: true },
};

export const vocabOf = (partner) => {
  if (partner && partner.vocab && partner.vocab.key) return partner.vocab;
  if (isRoad(partner)) return FALLBACK.road;
  if (typesOf(partner).includes('hotel')) return FALLBACK.hotel;
  if (isFood(partner)) return FALLBACK.food;
  if (typesOf(partner).includes('travel_agency')) return FALLBACK.travel;
  if (typesOf(partner).some((t) => ['service_provider', 'professional'].includes(t))) return FALLBACK.service;
  return FALLBACK.shop;
};

export const kindOf = (partner) => vocabOf(partner).key;

/** Every kind the business is, main one first - ['travel', 'hotel'] for a tour operator with rooms. */
export const kindsOf = (partner) => {
  if (partner && Array.isArray(partner.kinds) && partner.kinds.length) return partner.kinds;
  const main = kindOf(partner);
  const out = [main];
  if (main !== 'road') {
    if (typesOf(partner).includes('hotel')) out.push('hotel');
    if (isFood(partner)) out.push('food');
  }
  return [...new Set(out)];
};

export const hasKind = (partner, kind) => kindsOf(partner).includes(kind);

/**
 * Which catalogue tabs the business needs, from every side it has: a tour
 * operator with rooms gets services + rooms, a hotel with a restaurant gets
 * rooms + menu, a tyre shop gets services + products.
 */
export const sidesOf = (partner) => {
  if (partner && partner.sides && typeof partner.sides === 'object') return partner.sides;
  const kinds = kindsOf(partner).map((k) => FALLBACK[k]).filter(Boolean);
  const has = (what) => kinds.some((k) => k.sells === what || (what !== 'rooms' && k.sells === 'both'));
  return { products: has('products'), services: has('services'), rooms: has('rooms'), menu: kinds.some((k) => k.key === 'food'), jobs: true };
};

/** Units for a product or a service, from the first side of the business that sells one. */
export const unitsFor = (partner, what) => {
  const kinds = kindsOf(partner).map((k) => (partner && partner.vocab && partner.vocab.key === k ? partner.vocab : FALLBACK[k])).filter(Boolean);
  if (what === 'product') {
    const k = kinds.find((v) => v.sells === 'products') || kinds.find((v) => v.sells === 'both');
    return k ? (k.productUnits || k.units || []) : [];
  }
  const k = kinds.find((v) => v.sells === 'services' || v.sells === 'both');
  return k ? (k.units || []) : [];
};
/** The words for one of the business's sides, e.g. the hotel side of a tour operator. */
export const vocabFor = (kind) => FALLBACK[kind] || FALLBACK.shop;

export const VEHICLE_TYPES = [
  { value: 'bike', label: '🏍️ Bike', wheels: 'two' },
  { value: 'scooter', label: '🛵 Scooter', wheels: 'two' },
  { value: 'auto', label: '🛺 Auto', wheels: 'three' },
  { value: 'erickshaw', label: '🔋 E-rickshaw', wheels: 'three' },
  { value: 'cab', label: '🚗 Cab', wheels: 'four' },
  { value: 'taxi', label: '🚕 Taxi', wheels: 'four' },
  { value: 'van', label: '🚐 Van / tempo', wheels: 'four' },
];
export const FUEL_TYPES = ['petrol', 'diesel', 'cng', 'electric', 'hybrid'];

/** One line the customer and admin read: "Maruti Dzire · HR02AB1234 · White · 5 seater". */
export const vehicleLine = (v = {}) => [
  [v.brand, v.model].filter(Boolean).join(' '), v.number, v.colour, v.year, v.seats ? `${v.seats} seater` : '',
].filter(Boolean).join(' · ');

export default vocabOf;

/**
 * Which vehicle-service module a category opens: 'two' for bike / scooter
 * work, 'car' for car work, '' for anything else.
 */
export const vehicleFamilyOf = (name = '') => {
  if (/car (service|servicing|wash|washing|repair|care)|four wheeler|denting|car ac/i.test(name)) return 'car';
  if (/bike (service|servicing|wash|washing|repair)|scooter|two wheeler|puncture/i.test(name)) return 'two';
  return '';
};
